import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import type { WorkOrderDetail } from '@/hooks/useWorkOrders';
import type { SendQuoteData } from '@/hooks/useWorkOrders';
import { useWhatsAppStatus } from '@/hooks/useWhatsAppSettings';
import { languageNames, supportedLanguages, type SupportedLanguage } from '@/lib/i18n';

interface SendQuoteDialogProps {
  open: boolean;
  workOrder: WorkOrderDetail;
  onConfirm: (data: SendQuoteData) => void;
  onCancel: () => void;
  loading?: boolean;
}

/**
 * "Lähetä tarjous" (#2651): channel, recipient and an optional covering
 * message. The recipient starts from the work order's contact details and
 * can be changed for this send only; the API falls back to the orderer party
 * when the field is left empty. The language of the email and the PDF starts
 * from the orderer's own language (#3632).
 */
export function SendQuoteDialog({ open, workOrder, onConfirm, onCancel, loading }: SendQuoteDialogProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');

  // Offer WhatsApp only when the workshop can actually send it (#3124); the
  // toggle stays visible but disabled so staff learn where to enable it.
  const whatsappStatus = useWhatsAppStatus(open);
  const whatsappEnabled = whatsappStatus.data?.enabled ?? false;

  const [channel, setChannel] = useState<'email' | 'whatsapp'>('email');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [language, setLanguage] = useState<SupportedLanguage>('fi');

  useEffect(() => {
    if (!open) return;
    setChannel(workOrder.contactEmail ? 'email' : workOrder.contactPhone && whatsappEnabled ? 'whatsapp' : 'email');
    setEmail(workOrder.contactEmail ?? '');
    setPhone(workOrder.contactPhone ?? '');
    setMessage('');
    setLanguage(ordererLanguage(workOrder));
  }, [open, workOrder, whatsappEnabled]);

  useEffect(() => {
    if (channel === 'whatsapp' && !whatsappEnabled) setChannel('email');
  }, [channel, whatsappEnabled]);

  const recipient = channel === 'email' ? email.trim() : phone.trim();
  const canSend = recipient !== '' && !loading;

  const handleConfirm = () => {
    if (!canSend) return;
    onConfirm({
      channel,
      ...(channel === 'email' ? { recipientEmail: recipient } : { recipientPhone: recipient }),
      ...(message.trim() ? { message: message.trim() } : {}),
      language,
    });
  };

  return (
    <ResponsiveDialog open={open} onClose={loading ? undefined : onCancel} mobileTitle={t('sendQuote.title')} maxWidth="sm" fullWidth>
      <DialogTitle>{t('sendQuote.title')}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <ToggleButtonGroup
          value={channel}
          exclusive
          onChange={(_event, value: 'email' | 'whatsapp' | null) => { if (value) setChannel(value); }}
          size="small"
          aria-label={t('sendQuote.channel')}
        >
          <ToggleButton value="email">
            <EmailIcon fontSize="small" sx={{ mr: 1 }} />
            {t('sendQuote.channelEmail')}
          </ToggleButton>
          <ToggleButton value="whatsapp" disabled={!whatsappEnabled}>
            <WhatsAppIcon fontSize="small" sx={{ mr: 1 }} />
            {t('sendQuote.channelWhatsapp')}
          </ToggleButton>
        </ToggleButtonGroup>
        {!whatsappEnabled && !whatsappStatus.isPending && (
          <Typography variant="caption" color="text.secondary">
            {t('sendQuote.whatsappUnavailable')}
          </Typography>
        )}

        {channel === 'email' ? (
          <TextField
            label={t('sendQuote.recipientEmail')}
            type="email"
            value={email}
            onChange={(event) => { setEmail(event.target.value); }}
            size="small"
            fullWidth
            required
          />
        ) : (
          <TextField
            label={t('sendQuote.recipientPhone')}
            type="tel"
            value={phone}
            onChange={(event) => { setPhone(event.target.value); }}
            size="small"
            fullWidth
            required
          />
        )}

        <TextField
          select
          label={t('sendQuote.language')}
          helperText={t('sendQuote.languageHint')}
          value={language}
          onChange={(event) => { setLanguage(event.target.value as SupportedLanguage); }}
          size="small"
          fullWidth
        >
          {supportedLanguages.map((lng) => (
            <MenuItem key={lng} value={lng}>
              {languageNames[lng]}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label={t('sendQuote.message')}
          helperText={t('sendQuote.messageHint')}
          value={message}
          onChange={(event) => { setMessage(event.target.value); }}
          multiline
          minRows={3}
          size="small"
          fullWidth
          slotProps={{ htmlInput: { maxLength: 1000 } }}
        />

        <Alert severity="info" variant="outlined">
          {t('sendQuote.whatHappens')}
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>{tc('cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!canSend}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          {t('sendQuote.send')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}

/** The orderer's language when it is one we print in, else Finnish. */
function ordererLanguage(workOrder: WorkOrderDetail): SupportedLanguage {
  const preferred = workOrder.parties?.find((party) => party.role === 'orderer')?.preferredLanguage?.slice(0, 2);
  return supportedLanguages.find((lng) => lng === preferred) ?? 'fi';
}
